import { TFunction } from 'i18next'
import { z } from 'zod'

import { Trainer } from '@/api/trainers'
import { EmailLink } from '@/components/ui/email-link'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { PhoneLink } from '@/components/ui/phone-link'
import { SelectOption } from '@/components/ui/select'
import { SortOrderSchema, TableColumn } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants/ui'
import { formatDateOnly } from '@/utils/date'

import { ActiveStatusBadge } from '../shared/active-status-badge'
import { CountBadgeTooltip } from '../shared/count-badge-tooltip'
import { PersonNameCell } from '../shared/person-name-cell'
import { TrainerSortKey, TrainerSortKeySchema } from './staff.sort-keys'
import { TrainerStatusFilter, TrainerStatusFilterSchema } from './staff.status-filter'
import { getTrainerGenderLabel, getTrainerServices } from './staff.utils'

export const STAFF_TABLE_ID = 'staff-table'

export const staffTableFiltersSchema = z.object({
  buildingIds: z.array(z.coerce.number()).optional(),
  fromDate: z.coerce.number().optional(),
  limit: z.coerce.number().optional(),
  search: z.string().optional(),
  skip: z.coerce.number().optional(),
  sortBy: TrainerSortKeySchema.optional(),
  sortOrder: SortOrderSchema.optional(),
  status: TrainerStatusFilterSchema.optional(),
  toDate: z.coerce.number().optional(),
})

export type StaffTableFilters = z.infer<typeof staffTableFiltersSchema>

export const staffTableInitialFilters: StaffTableFilters = {
  buildingIds: undefined,
  fromDate: undefined,
  limit: DEFAULT_LIMIT,
  search: '',
  skip: 0,
  sortBy: TrainerSortKey.createdAt,
  sortOrder: 'desc',
  status: TrainerStatusFilter.all,
  toDate: undefined,
}

export const getStatusFilterOptions = (
  t: TFunction<['organisation']>,
): SelectOption<TrainerStatusFilter>[] => [
  { label: t('organisation:staff.list.filters.status.all'), value: TrainerStatusFilter.all },
  { label: t('organisation:staff.list.filters.status.active'), value: TrainerStatusFilter.active },
  {
    label: t('organisation:staff.list.filters.status.inactive'),
    value: TrainerStatusFilter.inactive,
  },
]

export const getStaffTableColumns = (
  t: TFunction<['organisation']>,
  onOpenDetails: (trainerId: number) => void,
): TableColumn<Trainer>[] => [
  {
    id: TrainerSortKey.name,
    render: (trainer) => <PersonNameCell user={trainer.user} />,
    sortKey: TrainerSortKey.name,
    title: t('organisation:staff.list.columns.name'),
    width: '22rem',
  },
  {
    id: TrainerSortKey.email,
    render: (trainer) => (
      <Typography color="black500" size="text-small" weight="medium" breakLines={1}>
        <EmailLink email={trainer.user.email} />
      </Typography>
    ),
    sortKey: TrainerSortKey.email,
    title: t('organisation:staff.list.columns.email'),
    width: '22rem',
  },
  {
    id: TrainerSortKey.phone,
    render: (trainer) => (
      <Typography color="black500" size="text-small" weight="medium">
        <PhoneLink phone={trainer.phone} />
      </Typography>
    ),
    sortKey: TrainerSortKey.phone,
    title: t('organisation:staff.list.columns.phone'),
    width: '14rem',
  },
  {
    id: TrainerSortKey.gender,
    render: (trainer) => (
      <Typography color="black500" size="text-small" weight="medium">
        {getTrainerGenderLabel(t, trainer.gender) || EMPTY_VALUE}
      </Typography>
    ),
    sortKey: TrainerSortKey.gender,
    title: t('organisation:staff.list.columns.gender'),
    width: '10rem',
  },
  {
    id: TrainerSortKey.services,
    render: (trainer) => {
      const services = getTrainerServices(trainer)
      return services.length ? (
        <CountBadgeTooltip
          badgeLabel={t('organisation:staff.list.servicesCount', { count: services.length })}
          items={services.map((service) => service.serviceId.name)}
          moreLabel={(count) => t('organisation:staff.list.moreServices', { count })}
          onMoreClick={() => onOpenDetails(trainer.id)}
        />
      ) : (
        <Typography color="black500" size="text-small" weight="medium">
          {EMPTY_VALUE}
        </Typography>
      )
    },
    sortKey: TrainerSortKey.services,
    title: t('organisation:staff.list.columns.services'),
    width: '13rem',
  },
  {
    id: TrainerSortKey.clients,
    render: (trainer) => (
      <Typography color="black500" size="text-small" weight="medium">
        {trainer.clients.length}
      </Typography>
    ),
    sortKey: TrainerSortKey.clients,
    title: t('organisation:staff.list.columns.clients'),
    width: '9rem',
  },
  {
    id: TrainerSortKey.status,
    render: (trainer) => <ActiveStatusBadge isActive={trainer.active} />,
    sortKey: TrainerSortKey.status,
    title: t('organisation:staff.list.columns.status'),
    width: '11rem',
  },
  {
    id: TrainerSortKey.createdAt,
    render: (trainer) => (
      <Typography color="black500" size="text-small" weight="medium">
        {formatDateOnly(new Date(trainer.createdAt))}
      </Typography>
    ),
    sortKey: TrainerSortKey.createdAt,
    title: t('organisation:staff.list.columns.createdAt'),
    width: '11rem',
  },
]
